import { useMemo } from "react";
import { useCenter } from "./useCenter";
import type { Center, SessionMode } from "../domain/Center";

export interface SessionPreferences {
    /** Whether therapy type and template are offered when creating a session. */
    therapyFieldsEnabled: boolean;
    /** Which kinds of session may be offered anywhere. */
    modes: Record<SessionMode, boolean>;
}

const EVERYTHING: SessionPreferences = {
    therapyFieldsEnabled: true,
    modes: { individual: true, couple: true, group: true },
};

/**
 * How the caller's centre wants sessions created. A therapist who works alone
 * keeps everything: these are a centre's decisions, and they have none.
 */
export function sessionPreferencesOf(center: Center | null | undefined): SessionPreferences {
    if (!center) return EVERYTHING;
    return {
        therapyFieldsEnabled: center.therapy_fields_enabled !== false,
        modes: {
            individual: center.individual_sessions_enabled !== false,
            couple: center.couple_sessions_enabled !== false,
            group: center.group_sessions_enabled !== false,
        },
    };
}

/**
 * Whether a subject of that kind may be offered: a patient (or an individual)
 * under the individual mode, a couple or a group under its own. A subject with
 * no kind is a patient.
 */
export function isSubjectOffered(
    modes: Record<SessionMode, boolean>,
    kind: "patient" | SessionMode | undefined
): boolean {
    return modes[!kind || kind === "patient" ? "individual" : kind];
}

/** The subject kinds a list may offer, in their usual order. */
export function offeredSubjectKinds(
    modes: Record<SessionMode, boolean>
): ("patient" | "couple" | "group")[] {
    return (["patient", "couple", "group"] as const).filter((kind) => isSubjectOffered(modes, kind));
}

/**
 * A caseload with the couples or groups the centre does not run emptied out.
 * Its people stay: a person is somebody a tool can be used with whether or
 * not the centre holds one-to-one sessions. Fields the caseload does not
 * carry are left absent.
 */
export function offeredCaseload<T extends { couples?: unknown[]; groups?: unknown[] }>(
    caseload: T,
    modes: Record<SessionMode, boolean>
): T {
    return {
        ...caseload,
        ...(caseload.couples && !modes.couple ? { couples: [] } : {}),
        ...(caseload.groups && !modes.group ? { groups: [] } : {}),
    };
}

export const useSessionPreferences = (): SessionPreferences => {
    const { center } = useCenter();
    const prefs = sessionPreferencesOf(center);
    const { individual, couple, group } = prefs.modes;
    // One object for as long as the choices stand, so effects can depend on it.
    return useMemo(
        () => ({ therapyFieldsEnabled: prefs.therapyFieldsEnabled, modes: { individual, couple, group } }),
        [prefs.therapyFieldsEnabled, individual, couple, group]
    );
};
